/**
* @summary - Contract management List
* @author Yuvasri S
* @since 2024-11-29
*/
import React, { useState, useEffect, act } from 'react';
import PageTitle from '../commoncomponents/PageTitle';
import { DefaultCard } from '../../assets/styled-components/card';
import { Box, Button, Divider, Grid, IconButton, Stack, styled, Typography, useMediaQuery } from '@mui/material';
import SectionTitle from '../commoncomponents/SectionTitle';
import CreateNewBtn from '../commoncomponents/CreateNewBtn';
import RefreshExportButtons from '../commoncomponents/RefreshExportButtons';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import CloseButton from '../commoncomponents/CloseButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorChips, SuccessChips } from "../../assets/styled-components/chips";
import ContractManagementRepository from "../../api/repositories/contractManagementRepository";
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CustomDialog from '../commoncomponents/CustomDialog';
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import CustomTooltip from '../commoncomponents/CustomTooltip';
import InactiveContractModalPopPup from './InactiveContractModalPopPup';

const ContractManagementList = () => {

    const StyledCardTab = styled(Button)(({ theme }) => ({
        padding: "16px",
        flexDirection: "column",
        flex: 1,
        alignItems: "start",
        borderRadius: 0,
        gap: "4px",
        textTransform: "capitalize",
        "&.active": {
            backgroundColor: "#F6F2FA",
            "& .count": {
                color: theme.palette.primary.main,
            },
        },
        "&:hover": {
            "& .count": {
                color: theme.palette.primary.main,
            },
        },
    }));
    const location = useLocation();
    const [rowData, setRowData] = useState([]);
    const [gridApi, setGridApi] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(location?.state?.tab || 0);

    const [allCount, setAllCount] = useState(0);
    const [expiredCount, setExpiredCount] = useState(0);
    const [activeCount, setActiveCount] = useState(0);
    const [expired6monthsCount, setExpired6MonthsCount] = useState(0);
    const [expired3monthsCount, setExpired3MonthsCount] = useState(0);
    const [expired1monthCount, setExpired1MonthCount] = useState(0);
    const [expired1weekCount, setExpired1WeekCount] = useState(0);

    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));


    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [inactiveDialogModal, setInactiveDialogModal] = useState(false);
    const [contractInactiveDialog, setContractInactiveDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const accessData = useAccess();
    const access = utilities.getScreenPermissions(accessData, "ENGT005");

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const data = await ContractManagementRepository.contractManagementList(activeTab);
            setRowData(data.map((item) => ({
                ...item,

                enddate: utilities.formatDate({ dateString: item?.enddate, format: "dd/mm/yyyy", dbdate: true }),
            })));
        } catch (error) {
            console.error("Error fetching contract data:", error);
        } finally {
            setIsLoading(false);
        }
    };


    const fetchDatacount = async () => {
        setIsLoading(true);
        try {
            const data = await ContractManagementRepository.contractManagementcount();
            setRowData(data.map((item) => ({
                ...item,

            })));
            setAllCount((data[0]?.allCount + data[1]?.allCount) || 0);
            setActiveCount((data[0]?.activeCount + data[1]?.activeCount) || 0);
            setExpiredCount((data[0]?.expiredcontract + data[1]?.expiredcontract) || 0);
            setExpired6MonthsCount((data[0]?.contract6Month + data[1]?.contract6Month) || 0);
            setExpired3MonthsCount((data[0]?.contract3Month + data[1]?.contract3Month) || 0);
            setExpired1MonthCount((data[0]?.contract1Month + data[1]?.contract1Month) || 0);
            setExpired1WeekCount((data[0]?.contract1Week + data[1]?.contract1Week) || 0);
        } catch (error) {
            console.error("Error fetching contract data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = (row) => {
        setSelectedRow(row);
        setDeleteDialogOpen(true);
    };


    const handleConfirmDelete = async () => {
        try {
            if (selectedRow?.id) {
                await ContractManagementRepository.deleteContractManagement(selectedRow?.serviceDesc, selectedRow?.id);
                setRowData((prevData) =>
                    prevData.filter((item) => item.id !== selectedRow.id)
                );

                // Show success dialog
                setConfirmationDialogOpen(true);
            } else {
                console.error("Cannot delete: selectedRow does not have an ID");
            }
        } catch (error) {
            console.error("Error deleting contract:", error);
        } finally {
            // Close delete confirmation dialog regardless of success or failure
            setDeleteDialogOpen(false);
            setSelectedRow(null);
        }
    };


    const handleEdit = (rowData) => {

        if (rowData?.newcontractid) {
            navigate("register", { state: { ...rowData, readOnly: false, editmode: true, viewMode: false, title: "Edit Contract Management", renewaledContract: true } });
        }
        else {
            navigate("register", { state: { ...rowData, readOnly: true, editmode: true, viewMode: false, contractMode: true, title: "Edit Contract Management" } });
        }
    };

    const handleRenewalForm = (rowData) => {
        // if (rowData?.newcontractid) {
        navigate("register", { state: { ...rowData, readOnly: false, editmode: true, viewMode: false, isRenewalForm: true, title: "Renewal Contract Management" } });
        // }
    };

    const handleView = (rowData) => {
        if (rowData?.newcontractid) {
            navigate("register", { state: { ...rowData, viewMode: true, title: "View Contract Management" } });
        }
        else {
            navigate("register", { state: { ...rowData, viewMode: true, title: "View Contract Management" } });
        }

    };

    const handleCreateClick = () => {
        navigate("register", { state: { readOnly: false, editmode: false, createMode: true, title: "Create Contract Management" } });

    };

    useEffect(() => {
        fetchData();  // Fetch data when the component mounts or activeTab changes 
        fetchDatacount();  // Fetch data when the component mounts or activeTab changes
    }, [activeTab]);

    const handleTabChange = (tabIndex) => {
        setActiveTab(tabIndex);  // Update the active tab

    };

    const handleOpenInactive = (rowData) => {
        setInactiveDialogModal(true);
        setSelectedRow(rowData);
    };

    const handleCloseInactiveDialog =() => {
        setInactiveDialogModal(false);
    };

    const handleConfirmContractInactive = () => {
        setInactiveDialogModal(false);
        setContractInactiveDialog(true);
    };

    const handleCloseContractInactiveAlertDialog = () => {
        setContractInactiveDialog(false);
        fetchData();
        fetchDatacount();
    };


    const colDefs = [
        {
            field: "serviceDesc",
            headerName: "Service"
        },
        {
            field: "contractno",
            headerName: "Contract No",
            cellRenderer: (params) => {
                const isActive = params.data.isactive;
                const isRenewed = params?.data?.isrenewal ? true : false;
                const isExpired = params?.data?.isactiveformate === "Expired" ? true : false;

                // Apply hyperlink only if conditions are met
                if (isActive && access?.canEdit && !isExpired) {
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                handleEdit(params.data);
                            }}
                            style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
                        >
                            {params.value}
                        </a>
                    );
                } else {
                    return params.value || ""; // Show plain text if conditions are not met
                }
            }
        },
        {
            field: "vendorname",
            headerName: "Vendor Name"
        },
        {
            field: "vendorcode",
            headerName: "Vendor Code"
        },
        {
            field: "contracttype",
            headerName: "Contract Type",

        },
        {
            field: "enddate",
            headerName: "Expired Date",

        },

        {
            field: "totalcontractvalue",
            headerName: "Total Contract Value(RM)",

        },
        {
            field: "rating",
            headerName: "Rating",
            cellRenderer: (params) => {
                if (params.value == null) return "";
                const maxStars = 5; // Maximum number of stars

                const filledStars = Array.from({ length: params.value }, (_, i) => (
                    <StarIcon key={`filled-${i}`} style={{ color: '#FFD700' }} />
                )); // Filled stars with gold color

                const emptyStars = Array.from(
                    { length: maxStars - params.value },
                    (_, i) => <StarBorderIcon key={`empty-${i}`} style={{ color: '#FFD700' }} />
                ); // Empty stars with gold color

                return [...filledStars, ...emptyStars]; // Combine filled and empty stars
            },
        },
        {
            field: "isactiveformate", headerName: "Status",
            cellRenderer: (params) => {
                if (params.value === "Active") {
                    return <SuccessChips label="Active" />;
                } else {
                    return <ErrorChips label={params?.value} />;
                }
            }
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            cellClassName: 'actions',
            minWidth: 250,
            filter: false,
            cellRenderer: (params) => {
                const isActive = params.data.isactive;
                const isRenewed = params?.data?.isrenewal ? true : false;
                const isExpired = params?.data?.isactiveformate === "Expired" ? true : false;

                return (
                    <>
                        {isActive && access?.canEdit && !isExpired && (
                            <CustomTooltip title="Edit">
                                <IconButton
                                    onClick={() => handleEdit(params.data)}
                                    data-testid="edit-button"
                                >
                                    <EditIcon />
                                </IconButton>
                            </CustomTooltip>
                        )}
                        {access?.canEdit && (
                            <CustomTooltip title="View">
                                <IconButton
                                    onClick={() => handleView(params.data)}
                                    data-testid="view-button"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </CustomTooltip>
                        )}
                        {/* {isActive && access?.canDelete && (
                            <CustomTooltip title="Delete">
                                <IconButton
                                    onClick={() => handleDelete(params.data)}
                                    data-testid="delete-button"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </CustomTooltip>
                        )} */}
                        {(isActive && !isRenewed) && (
                            <Button
                                variant="contained"
                                sx={{ width: 30, height: 30 }}
                                onClick={() => handleRenewalForm(params.data)}
                            >
                                RENEW
                            </Button>
                        )} &nbsp;
                        {isExpired && (
                            <Button
                                variant="contained"
                                // sx={{ width: 45, height: 30 }}
                                onClick={() => handleOpenInactive(params.data)}
                            >
                                InActive
                            </Button>
                        )}
                    </>
                );
            },
        }

    ];


    return (
        <>
            <PageTitle title={"Contract Management"} />
            <Box mt={{ xs: 1, sm: 2 }}>
                <DefaultCard>
                    <Grid container spacing={2}>
                        <Grid item xs={13} md={9}>
                            <Stack
                                direction={"row"}
                                divider={<Divider orientation="vertical" flexItem />}
                                border={0.5}
                                borderColor={"divider"}
                                borderRadius={4}
                                overflow={"hidden"}
                                height={"100%"}
                            >
                                <StyledCardTab
                                    className={`${activeTab === 0 ? "active" : null}`}
                                    onClick={() => handleTabChange(0)}

                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        All Contract
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {allCount}
                                    </Typography>
                                </StyledCardTab>
                                <StyledCardTab
                                    className={`${activeTab === 1 ? "active" : null}`}
                                    onClick={() => handleTabChange(1)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Active Contract
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {activeCount}
                                    </Typography>
                                </StyledCardTab>
                                <StyledCardTab
                                    className={`${activeTab === 2 ? "active" : null}`}
                                    onClick={() => handleTabChange(2)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Expiring within 6 Months
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {expired6monthsCount}
                                    </Typography>
                                </StyledCardTab>
                                <StyledCardTab
                                    className={`${activeTab === 3 ? "active" : null}`}
                                    onClick={() => handleTabChange(3)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Expiring within 3 Months
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {expired3monthsCount}
                                    </Typography>
                                </StyledCardTab>
                                <StyledCardTab
                                    className={`${activeTab === 4 ? "active" : null}`}
                                    onClick={() => handleTabChange(4)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Expiring within 1 Month
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {expired1monthCount}
                                    </Typography>
                                </StyledCardTab>

                                <StyledCardTab
                                    className={`${activeTab === 5 ? "active" : null}`}
                                    onClick={() => handleTabChange(5)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Expiring within 1 Week
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {expired1weekCount}
                                    </Typography>
                                </StyledCardTab>
                                <StyledCardTab
                                    className={`${activeTab === 6 ? "active" : null}`}
                                    onClick={() => handleTabChange(6)}
                                >
                                    <Typography color={"text.primary"} fontSize={13} fontWeight={900}>
                                        Expired
                                    </Typography>
                                    <Typography
                                        className="count"
                                        color={"text.secondary"}
                                        fontSize={24}
                                        fontWeight={500}
                                    >
                                        {expiredCount}
                                    </Typography>
                                </StyledCardTab>

                            </Stack>
                        </Grid>
                    </Grid>
                </DefaultCard>
            </Box>
            <DefaultCard>
                <Stack
                    mb={1}
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                >
                    <SectionTitle title={"Contract Management List"} />

                    <Stack direction={"row"} spacing={1.5}>
                        {access?.canCreate && <CreateNewBtn
                            onClick={handleCreateClick}
                        />}
                        <RefreshExportButtons
                            pagination={false}
                            exportFunction={() => { }}
                            exportData={rowData}
                            refData={gridApi}
                            handleRefresh={fetchData}
                            isContract={true}
                            title={"Contract Management"}
                        />
                    </Stack>
                </Stack>
                <Box>
                    <CustomAgGrid
                        rows={rowData}
                        columns={colDefs}
                        ref={gridApi}
                        setGridApi={setGridApi}
                        isLoading={isLoading}
                    />
                </Box>
                <CloseButton />
                {/* Delete Confirmation Dialog */}
                <CustomDialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                    onConfirm={handleConfirmDelete}
                    content="Are you sure you want to delete?"
                    confirmBtn="Yes"
                    cancelBtn="No"
                />

                {/* Success Confirmation Dialog */}
                <CustomDialog
                    open={confirmationDialogOpen}
                    onClose={() => setConfirmationDialogOpen(false)}
                    onConfirm={() => setConfirmationDialogOpen(false)}
                    content="Record deleted successfully."
                    confirmBtn="OK"
                />

                <InactiveContractModalPopPup 
                    open={inactiveDialogModal}
                    onClose={handleCloseInactiveDialog}
                    onConfirm={handleConfirmContractInactive}
                    selectedRowData={selectedRow}
                />

                <CustomDialog
                    open={contractInactiveDialog}
                    onClose={handleCloseContractInactiveAlertDialog}
                    onConfirm={handleCloseContractInactiveAlertDialog}
                    content={`Contract Inactivated ${selectedRow?.contractno ? `- ${selectedRow?.contractno || ""}.` : "."}`}                    
                    confirmBtn="OK"
                />

            </DefaultCard>
        </>
    );
};

export default ContractManagementList;
